import type { TowerSchema } from "../types/project"

// Define tower property constants for consistent access
export const TOWER_PROPERTIES = {
    LOCATION_NO: "Loc No",
    STRUCTURE_NAME_AT_LOCATION: "Structure Name at Location",
    STRUCTURE_NAME: "Structure Name",
    DEV_ANGLE: 'Deviation ANGLE "+" Right "-" Left',
    IS_TENSION: "IsTension",

    BODY_EXTENSION: "Body Extension",
    LEG_EXTENSION_A: "Leg Extension @ A",
    LEG_EXTENSION_B: "Leg Extension @ B",
    LEG_EXTENSION_C: "Leg Extension @ C",
    LEG_EXTENSION_D: "Leg Extension @ D",
    WIND_SPAN: "Wind span",
    WEIGHT_SPAN_MIN_TEMP: "Weight span at minimum temperature",
    WEIGHT_SPAN_MAX_TEMP: "Weight span at maximum temperature",
    RAISED_CHIMNEY_A: "Raised chimney @ A",
    RAISED_CHIMNEY_B: "Raised chimney @ B",
    RAISED_CHIMNEY_C: "Raised chimney @ C",
    RAISED_CHIMNEY_D: "Raised chimney @ D",

    SOIL_TYPE: "Soil Type",
    FOUNDATION_TYPE_A: "Foundation Type @ A",
    FOUNDATION_TYPE_B: "Foundation Type @ B",
    FOUNDATION_TYPE_C: "Foundation Type @ C",
    FOUNDATION_TYPE_D: "Foundation Type @ D",
    SOIL_INVESTIGATION_AVAILABLE: "Soil Investigation Available?",

    HT_STEEL_MEMBERS: "HT Steel members",
    MS_STEEL_MEMBERS: "MS Steel members",
    HT_STEEL_PLATES: "HT Steel plates",
    MS_STEEL_PLATES: "MS Steel plates",
    DIA_16_BN: "16 dia B&N",
    DIA_20_BN: "20 dia B&N",
    PCC: "PCC",
    RCC: "RCC",
    EXCAVATION: "Excavation",
    REINFORCEMENT_MS_STEEL: "Reinforcement MS Steel",
    REINFORCEMENT_STEEL: "Reinforcement Steel",

    EAST: "EAST",
    NORTH: "NORTH",
    CENTER_POINT_ELEVATION: "Center point Elevation",
    GROUND_ELEVATION_A: "Ground elevation @ A",
    GROUND_ELEVATION_B: "Ground elevation @ B",
    GROUND_ELEVATION_C: "Ground elevation @ C",
    GROUND_ELEVATION_D: "Ground elevation @ D",
    TOWER_BASE_OCCUPIED_AREA: "Tower Base occupied area",
    CUTTING_OR_FILLING_VOLUME_A: "Cutting or Filling volume @ A",
    CUTTING_OR_FILLING_VOLUME_B: "Cutting or Filling volume @ B",
    CUTTING_OR_FILLING_VOLUME_C: "Cutting or Filling volume @ C",
    CUTTING_OR_FILLING_VOLUME_D: "Cutting or Filling volume @ D",
    FOOTING_AREA_A: "Footing Area A",
    FOOTING_AREA_B: "Footing Area B",
    FOOTING_AREA_C: "Footing Area C",
    FOOTING_AREA_D: "Footing Area D",
    BENCHING_VOLUME: "Benching volume",
    LATITUDE: "Latitude",
    LONGITUDE: "Longitude",
}

// Define tower property groups for UI organization
export const TOWER_PROPERTY_GROUPS = {
    "Basic Information": [
        TOWER_PROPERTIES.LOCATION_NO,
        TOWER_PROPERTIES.STRUCTURE_NAME_AT_LOCATION,
        TOWER_PROPERTIES.STRUCTURE_NAME,
        TOWER_PROPERTIES.DEV_ANGLE,
        TOWER_PROPERTIES.IS_TENSION,
    ],
    "Structure Details": [
        TOWER_PROPERTIES.BODY_EXTENSION,
        TOWER_PROPERTIES.LEG_EXTENSION_A,
        TOWER_PROPERTIES.LEG_EXTENSION_B,
        TOWER_PROPERTIES.LEG_EXTENSION_C,
        TOWER_PROPERTIES.LEG_EXTENSION_D,
        TOWER_PROPERTIES.WIND_SPAN,
        TOWER_PROPERTIES.WEIGHT_SPAN_MIN_TEMP,
        TOWER_PROPERTIES.WEIGHT_SPAN_MAX_TEMP,
        TOWER_PROPERTIES.RAISED_CHIMNEY_A,
        TOWER_PROPERTIES.RAISED_CHIMNEY_B,
        TOWER_PROPERTIES.RAISED_CHIMNEY_C,
        TOWER_PROPERTIES.RAISED_CHIMNEY_D,
    ],
    "Foundation Details": [
        TOWER_PROPERTIES.SOIL_TYPE,
        TOWER_PROPERTIES.FOUNDATION_TYPE_A,
        TOWER_PROPERTIES.FOUNDATION_TYPE_B,
        TOWER_PROPERTIES.FOUNDATION_TYPE_C,
        TOWER_PROPERTIES.FOUNDATION_TYPE_D,
        TOWER_PROPERTIES.SOIL_INVESTIGATION_AVAILABLE,
    ],
    "Material Information": [
        TOWER_PROPERTIES.HT_STEEL_MEMBERS,
        TOWER_PROPERTIES.MS_STEEL_MEMBERS,
        TOWER_PROPERTIES.HT_STEEL_PLATES,
        TOWER_PROPERTIES.MS_STEEL_PLATES,
        TOWER_PROPERTIES.DIA_16_BN,
        TOWER_PROPERTIES.DIA_20_BN,
        TOWER_PROPERTIES.PCC,
        TOWER_PROPERTIES.RCC,
        TOWER_PROPERTIES.EXCAVATION,
        TOWER_PROPERTIES.REINFORCEMENT_MS_STEEL,
        TOWER_PROPERTIES.REINFORCEMENT_STEEL,
    ],
    "Elevation & Ground Data": [
        TOWER_PROPERTIES.EAST,
        TOWER_PROPERTIES.NORTH,
        TOWER_PROPERTIES.CENTER_POINT_ELEVATION,
        TOWER_PROPERTIES.GROUND_ELEVATION_A,
        TOWER_PROPERTIES.GROUND_ELEVATION_B,
        TOWER_PROPERTIES.GROUND_ELEVATION_C,
        TOWER_PROPERTIES.GROUND_ELEVATION_D,
        TOWER_PROPERTIES.TOWER_BASE_OCCUPIED_AREA,
        TOWER_PROPERTIES.CUTTING_OR_FILLING_VOLUME_A,
        TOWER_PROPERTIES.CUTTING_OR_FILLING_VOLUME_B,
        TOWER_PROPERTIES.CUTTING_OR_FILLING_VOLUME_C,
        TOWER_PROPERTIES.CUTTING_OR_FILLING_VOLUME_D,
        TOWER_PROPERTIES.FOOTING_AREA_A,
        TOWER_PROPERTIES.FOOTING_AREA_B,
        TOWER_PROPERTIES.FOOTING_AREA_C,
        TOWER_PROPERTIES.FOOTING_AREA_D,
        TOWER_PROPERTIES.BENCHING_VOLUME,
        TOWER_PROPERTIES.LATITUDE,
        TOWER_PROPERTIES.LONGITUDE,
    ],
}

/**
 * Gets all tower properties including dynamic ones from the schema
 * @param schema The tower schema containing property definitions
 * @returns Array of property titles including both static and dynamic properties
 */
export const getAllTowerProperties = (schema?: TowerSchema) => {
    if (!schema) return Object.values(TOWER_PROPERTIES)

    const dynamicProperties = Object.entries(schema)
        .filter(([_, prop]) => (prop as any).isDynamic === true)
        .map(([_, prop]) => prop.title)

    return [...Object.values(TOWER_PROPERTIES), ...dynamicProperties]
}

// Helper function to get property value with fallback
export const getPropertyValue = (properties: Record<string, any>, propertyKey: string) => {
    // Try direct access first
    if (properties[propertyKey] !== undefined) {
        return properties[propertyKey]
    }

    // Try case-insensitive match
    const lowerKey = propertyKey.toLowerCase()
    const matchingKey = Object.keys(properties).find((key) => key.toLowerCase() === lowerKey)

    if (matchingKey) {
        return properties[matchingKey]
    }

    // Try to match by removing special characters
    const normalizedKey = propertyKey.replace(/[@&?!]/g, "").trim()
    const normalizedMatch = Object.keys(properties).find(
        (key) =>
            key
                .replace(/[@&?!]/g, "")
                .trim()
                .toLowerCase() === normalizedKey.toLowerCase(),
    )

    if (normalizedMatch) {
        return properties[normalizedMatch]
    }

    return undefined
}

